import * as React from "react";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  FormControl,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import AnimateButton from "components/animateButton";
import CarCompaniesDropdown from "components/carCompaniesDropdown";
import VehiclesDropdown from "components/vehiclesDropdown";
import Loader from "components/loader";
import { addVichleToUser } from "modules/company/action";
import SHOW_TOAST from "utils/showToast";
import { BRAND } from "themes";

type Props = {
  state: boolean;
  setState: (open: boolean) => void;
  userId: string;
  /** "0" = Car, "1" = Bike — filters vehicle list like create-order category */
  vehicleType?: string | number;
  onSuccess?: (result?: any, selected?: {
    vehicleId: string;
    vehicleBrandId: string;
    vehicleType: number;
  }) => void;
};

export default function AddUserVehicleModal({
  state,
  setState,
  userId,
  vehicleType = "0",
  onSuccess,
}: Props) {
  const theme = useTheme();
  const dispatch: any = useDispatch();

  const [loading, setLoading] = React.useState(false);
  const [pickerKey, setPickerKey] = React.useState(0);
  const [vehicleBrandId, setVehicleBrandId] = React.useState("");
  const [vehicleId, setVehicleId] = React.useState("");
  const [selectedVehicleType, setSelectedVehicleType] = React.useState<
    string | number | ""
  >("");

  const secondary = theme.palette.secondary.main || BRAND.secondary;
  const secondaryDark = theme.palette.secondary.dark || BRAND.primaryDark;
  const secondaryLight = theme.palette.secondary.light || "#ede7f6";
  const border = theme.palette.divider || BRAND.border;
  const text = theme.palette.text.primary || BRAND.ink;
  const muted = theme.palette.text.secondary || BRAND.muted;
  const surfaceMuted = theme.palette.grey[50] || BRAND.track;

  const resetDraft = () => {
    setVehicleBrandId("");
    setVehicleId("");
    setSelectedVehicleType("");
    setPickerKey((key) => key + 1);
  };

  React.useEffect(() => {
    if (state) {
      resetDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleClose = () => {
    setState(false);
  };

  const handleSubmit = () => {
    if (!userId) {
      SHOW_TOAST("Please select a user first");
      return;
    }
    if (!vehicleBrandId || !vehicleId) {
      SHOW_TOAST("Please select car company and vehicle");
      return;
    }

    const resolvedType =
      selectedVehicleType !== "" && selectedVehicleType != null
        ? Number(selectedVehicleType)
        : Number(vehicleType);

    const payload = {
      vehicleId,
      vehicleBrandId,
      userId,
      vehicleType: resolvedType,
    };

    setLoading(true);
    dispatch(
      addVichleToUser({
        data: payload,
        callback: (result?: any) => {
          setLoading(false);
          if (!result) return;
          setState(false);
          resetDraft();
          onSuccess?.(result, {
            vehicleId: payload.vehicleId,
            vehicleBrandId: payload.vehicleBrandId,
            vehicleType: payload.vehicleType,
          });
        },
      }),
    );
  };

  const modalSx = {
    root: {
      position: "absolute" as const,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { xs: "92%", sm: 480 },
      maxHeight: "90vh",
      overflow: "auto",
      bgcolor: "background.paper",
      borderRadius: "20px",
      border: `1px solid ${border}`,
      boxShadow:
        "0 24px 48px rgba(27, 19, 64, 0.14), 0 8px 16px rgba(27, 19, 64, 0.06)",
      outline: "none",
    },
    header: {
      position: "relative" as const,
      px: 3,
      pt: 3,
      pb: 2.5,
      background: `linear-gradient(135deg, ${secondaryLight} 0%, #FFFFFF 58%, ${surfaceMuted} 100%)`,
      borderBottom: `1px solid ${border}`,
    },
    accentBar: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${secondary} 100%)`,
      borderRadius: "20px 20px 0 0",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      mb: 1.25,
      borderRadius: "999px",
      bgcolor: secondaryLight,
      color: secondaryDark,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.02em",
    },
    title: {
      color: text,
      fontWeight: 700,
      fontSize: { xs: 22, sm: 24 },
      lineHeight: 1.25,
      letterSpacing: "-0.02em",
    },
    subtitle: {
      mt: 0.75,
      color: muted,
      fontSize: 14,
      lineHeight: 1.5,
    },
    closeBtn: {
      position: "absolute" as const,
      top: 14,
      right: 14,
      color: muted,
      bgcolor: "rgba(255,255,255,0.85)",
      border: `1px solid ${border}`,
      transition: "all 200ms ease",
      "&:hover": {
        color: text,
        bgcolor: surfaceMuted,
        borderColor: theme.palette.grey[300],
      },
    },
    body: {
      px: 3,
      py: 2.5,
      display: "flex",
      flexDirection: "column" as const,
      gap: 2.25,
    },
    fieldLabel: {
      mb: 0.75,
      color: text,
      fontSize: 13,
      fontWeight: 600,
    },
    fieldShell: {
      width: "100%",
      "& .MuiOutlinedInput-root, & .MuiAutocomplete-root .MuiOutlinedInput-root":
        {
          borderRadius: "12px",
          bgcolor: "#fff",
          transition: "box-shadow 200ms ease",
          "& fieldset": {
            borderColor: border,
          },
          "&:hover fieldset": {
            borderColor: theme.palette.grey[400],
          },
          "&.Mui-focused fieldset": {
            borderColor: secondary,
            borderWidth: "1.5px",
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${secondary}22`,
          },
        },
    },
    footer: {
      px: 3,
      py: 2,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 1.5,
      borderTop: `1px solid ${border}`,
      bgcolor: surfaceMuted,
    },
    cancelBtn: {
      minWidth: 100,
      borderRadius: "12px",
      textTransform: "none" as const,
      fontWeight: 600,
      color: muted,
      borderColor: border,
      "&:hover": {
        borderColor: theme.palette.grey[400],
        bgcolor: "#fff",
        color: text,
      },
    },
    submitBtn: {
      minWidth: 150,
      borderRadius: "12px",
      textTransform: "none" as const,
      fontWeight: 700,
      boxShadow: `0 8px 20px ${secondary}40`,
      "&:hover": {
        boxShadow: `0 10px 24px ${secondary}55`,
      },
    },
  };

  return (
    <Modal
      open={state}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 250,
        sx: {
          backgroundColor: "rgba(27, 19, 64, 0.45)",
          backdropFilter: "blur(4px)",
        },
      }}
      aria-labelledby="add-user-vehicle-modal-title"
      aria-describedby="add-user-vehicle-modal-description"
    >
      <Fade in={state}>
        <Box sx={modalSx.root}>
          <Box sx={modalSx.header}>
            <Box sx={modalSx.accentBar} />
            <Box sx={modalSx.badge}>Assign to customer</Box>
            <Typography
              id="add-user-vehicle-modal-title"
              sx={modalSx.title}
              component="h2"
            >
              Add Vehicle
            </Typography>
            <Typography
              id="add-user-vehicle-modal-description"
              sx={modalSx.subtitle}
            >
              Select a company and vehicle model to add it to this customer’s
              profile.
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={modalSx.closeBtn}
              aria-label="Close"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={modalSx.body}>
            <Box sx={modalSx.fieldShell}>
              <Typography sx={modalSx.fieldLabel}>Company</Typography>
              <FormControl fullWidth>
                <CarCompaniesDropdown
                  key={`car-company-${pickerKey}`}
                  onSelectCarCompany={(carCompany: any) => {
                    setVehicleBrandId(
                      carCompany?.id || carCompany?._id || "",
                    );
                    setVehicleId("");
                    setSelectedVehicleType("");
                  }}
                />
              </FormControl>
            </Box>

            <Box sx={modalSx.fieldShell}>
              <Typography sx={modalSx.fieldLabel}>Vehicle</Typography>
              <FormControl fullWidth>
                <VehiclesDropdown
                  key={`vehicle-${pickerKey}-${vehicleBrandId}`}
                  companyId={vehicleBrandId}
                  vehicleType={String(vehicleType)}
                  onSelectVehicle={(vehicle: any) => {
                    setVehicleId(
                      vehicle?.vehicleId || vehicle?.id || vehicle?._id || "",
                    );
                    setSelectedVehicleType(
                      vehicle?.vehicleType === 0 || vehicle?.vehicleType
                        ? Number(vehicle.vehicleType)
                        : Number(vehicleType),
                    );
                  }}
                />
              </FormControl>
            </Box>
          </Box>

          <Box sx={modalSx.footer}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={modalSx.cancelBtn}
            >
              Cancel
            </Button>
            <AnimateButton>
              <Button
                disableElevation
                size="large"
                variant="contained"
                color="secondary"
                disabled={!vehicleBrandId || !vehicleId || loading}
                onClick={handleSubmit}
                sx={modalSx.submitBtn}
              >
                Add Vehicle
              </Button>
            </AnimateButton>
          </Box>

          {loading && <Loader />}
        </Box>
      </Fade>
    </Modal>
  );
}
